import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Copy, CreditCard, ImagePlus, UserCog, UserPlus } from 'lucide-react';
import { Button, Modal, cn } from '@techsofit/ui';

interface WelcomeCardProps {
  open: boolean;
  /** Close for this session (shows again next sign-in until setup is complete). */
  onClose: () => void;
  gymName: string;
  /** The gym's web address shown to the user, e.g. oxygen.gyms.techsofit.com (with port in dev). */
  host: string;
  /** Full origin to copy / bookmark. */
  origin: string;
  isOwner: boolean;
  hasLogo: boolean;
  hasPlans: boolean;
  hasMembers: boolean;
  /** A teammate beyond the owner has been invited (the optional step). */
  hasStaff: boolean;
}

interface Step {
  key: string;
  icon: typeof CreditCard;
  label: string;
  /** One line on why it's worth doing. */
  why: string;
  to: string;
  done: boolean;
  ownerOnly?: boolean;
  optional?: boolean;
}

/**
 * First-run welcome shown when an owner/staff lands on their gym's subdomain. It's a
 * quick tour plus a getting-started **checklist**: each step shows a tick once done
 * (logo set, a plan created, a first member added) so the owner can see what's left.
 * Inviting the team is optional. Shows on each sign-in until "Don't show this again".
 */
export function WelcomeCard({
  open,
  onClose,
  gymName,
  host,
  origin,
  isOwner,
  hasLogo,
  hasPlans,
  hasMembers,
  hasStaff,
}: WelcomeCardProps) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const steps: Step[] = (
    [
      { key: 'logo', icon: ImagePlus, label: 'Add your logo', why: 'Your members see your brand on the sign-in page, not ours.', to: '/settings', done: hasLogo },
      { key: 'plan', icon: CreditCard, label: 'Create a membership plan', why: 'Set your prices once, then assign a plan whenever you add a member.', to: '/plans', done: hasPlans },
      { key: 'member', icon: UserPlus, label: 'Add your first member', why: 'Start your roster. Record payments and send renewal reminders from day one.', to: '/members/new', done: hasMembers },
      { key: 'team', icon: UserCog, label: 'Invite your team', why: 'Let staff run the front desk without sharing your login.', to: '/staff', done: hasStaff, ownerOnly: true, optional: true },
    ] as Step[]
  ).filter((s) => !s.ownerOnly || isOwner);

  const required = steps.filter((s) => !s.optional);
  const doneCount = required.filter((s) => s.done).length;
  const allRequiredDone = doneCount === required.length;
  // The button points at the next thing worth doing — first unfinished required step,
  // else the first unfinished optional one.
  const next = steps.find((s) => !s.done && !s.optional) ?? steps.find((s) => !s.done);

  const go = (to: string) => {
    onClose();
    navigate(to);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(origin);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard blocked — the address is shown for manual copy anyway
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Welcome to ${gymName} on TechSoFit`}
      description="This address is your business's own home. Here's a quick tour to get you up and running."
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-medium text-muted hover:text-ink">
            {allRequiredDone ? 'Close' : 'Skip for now'}
          </button>
          {next && (
            <Button onClick={() => go(next.to)}>
              {next.label} <ArrowRight className="size-4" aria-hidden />
            </Button>
          )}
        </div>
      }
    >
      {/* Your web address */}
      <div className="rounded-control border border-line bg-surface p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted">Your web address</p>
        <div className="mt-2 flex items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-control bg-elevated px-3 py-2 font-mono text-sm text-ink">
            {host}
          </code>
          <button
            type="button"
            onClick={copy}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-control border border-line bg-elevated px-3 py-2 text-sm font-medium text-ink transition hover:border-line-strong"
          >
            {copied ? <Check className="size-4 text-good-ink" aria-hidden /> : <Copy className="size-4" aria-hidden />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <p className="mt-2 text-sm text-muted">
          Bookmark it. Next time, you and your members sign in straight from here. No need to start anywhere else.
        </p>
      </div>

      {/* Getting-started checklist — ticks off as each step is done. */}
      <div className="mt-5 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted">Get set up</p>
        <p className="text-xs font-medium text-muted">{doneCount} of {required.length} done</p>
      </div>
      <div className="mt-2 flex flex-col gap-2">
        {steps.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => go(s.to)}
            disabled={s.done}
            className={cn(
              'flex items-center gap-3 rounded-control border border-line px-3.5 py-3 text-left transition',
              s.done ? 'bg-surface' : 'bg-elevated hover:border-line-strong hover:shadow-card',
            )}
          >
            <span
              className={cn(
                'grid size-9 shrink-0 place-items-center rounded-full',
                s.done ? 'bg-accent text-accent-ink' : 'bg-canvas text-ink',
              )}
            >
              {s.done ? <Check className="size-4" aria-hidden /> : <s.icon className="size-4" aria-hidden />}
            </span>
            <span className="min-w-0 flex-1">
              <span className={cn('flex items-center gap-2 text-sm font-medium', s.done ? 'text-muted' : 'text-ink')}>
                <span className={s.done ? 'line-through' : undefined}>{s.label}</span>
                {s.optional && !s.done && (
                  <span className="rounded-pill bg-surface px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted">
                    Optional
                  </span>
                )}
              </span>
              {!s.done && <span className="block text-xs text-muted">{s.why}</span>}
            </span>
            {!s.done && <ArrowRight className="size-4 shrink-0 text-muted" aria-hidden />}
          </button>
        ))}
      </div>
    </Modal>
  );
}
